import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import _ from 'lodash';
import { environment } from 'src/environments/environment';
import { ProjectEnvironment, ProjectProfile } from '../../../models/projects.model';
import { ApiService } from '../../api.service';
import { ErrorBannerService } from '../../error-banner.service';
import { environmentSettingsConfig } from '../configs/environment-settings.config';
import { defaultFavicon } from '../configs/project-info.config';
import {
  AppLogoResponse,
  AppSettingsResponse,
  defaultConfig,
  ProjectInfo,
} from '../models/access-rules.model';
import { AccessRulesStateService } from './access-rules-state.service';

@Injectable({
  providedIn: 'root',
})
export class AccessRulesInitService {
  private readonly API_URL = 'app-settings/config';

  constructor(
    private apiService: ApiService,
    private stateService: AccessRulesStateService,
    private errorBannerService: ErrorBannerService,
    private ngZone: NgZone,
  ) {}

  getLogoInitital(): Promise<void> {
    return new Promise((resolve) => {
      this.apiService.get<AppLogoResponse>('app-settings/logo', {}, false).subscribe(
        (res: AppLogoResponse) => {
          const current = this.stateService.getState();
          this.stateService.updateState({
            ...current,
            projectName: res.tenantDisplayName || current.projectName,
            logoBase64: res.logoBase64 || current.logoBase64,
            compactLogoBase64: res.compactLogoBase64 || current.compactLogoBase64,
            faviconUrl: res.faviconUrl || current.faviconUrl,
          });
          resolve();
        },
        () => resolve(),
      );
    });
  }

  initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apiService.get(this.API_URL, { responseType: 'text' as any }).subscribe(
        (res: any) => {
          this.ngZone.runOutsideAngular(async () => {
            try {
              const decoded = await this.decrypt(res);
              this.ngZone.run(() => {
                this.processAppSettings(
                  decoded ? JSON.parse(decoded) : ({} as AppSettingsResponse),
                );
                resolve();
              });
            } catch (error) {
              this.ngZone.run(() => reject(error));
            }
          });
        },
        (error: HttpErrorResponse) => {
          if (
            error.status === 502 ||
            error.status === 503 ||
            error.status === 504 ||
            error.status === 0
          ) {
            this.errorBannerService.showServiceUnavailableBanner();
            // Continue initialization with default config instead of blocking
            this.processAppSettings({
              properties: { abbreviation: 'DEFAULT' },
              tenantDisplayName: 'Default',
              environment: 'DEV',
            } as AppSettingsResponse);
            resolve(); // Resolve instead of reject to continue app initialization
          } else {
            reject(error);
          }
        },
      );
    });
  }

  private processAppSettings(res: AppSettingsResponse): void {
    const profile = (res.properties?.abbreviation?.toUpperCase() ||
      ProjectProfile.DEFAULT) as ProjectProfile;

    let tenantDisplayName = res.tenantDisplayName || res.properties?.name || profile;
    let supportAbbreviation = 'CMS';

    if (profile === ProjectProfile.UTMC) {
      supportAbbreviation = 'UTMC';
      const desiredName = 'Unemployment Tax Management Corp';
      tenantDisplayName =
        res.tenantDisplayName === desiredName ? res.tenantDisplayName : desiredName;
    } else if (profile === ProjectProfile.STOPCLAIMS) {
      const desiredName = 'StopClaims Corp';
      tenantDisplayName =
        res.tenantDisplayName === desiredName ? res.tenantDisplayName : desiredName;
    }

    const environment = res.environment?.toUpperCase() || '';
    const validEnvironment = Object.values(ProjectEnvironment).includes(
      environment as ProjectEnvironment,
    )
      ? (environment as ProjectEnvironment)
      : ProjectEnvironment.DEV;

    this.stateService.updateState({
      profile,
      environment: validEnvironment,
      projectName: tenantDisplayName,
      logoBase64: res.logoBase64 ? res.logoBase64 : '',
      compactLogoBase64: res.compactLogoBase64 ? res.compactLogoBase64 : '',
      faviconUrl: res.faviconUrl ? res.faviconUrl : defaultFavicon,
      supportAbbreviation,
      features: _.merge({}, defaultConfig, res.config),
      projectInfo: {
        name: res.properties?.name || '',
        abbreviation: res.properties?.abbreviation?.toUpperCase() || '',
        phone: res.properties?.phone || '',
        fax: res.properties?.fax || '',
        email: res.properties?.email || '',
        address_line_one: res.properties?.address_line_one || '',
        address_line_two: res.properties?.address_line_two || '',
        url: res.properties?.url || '',
      },
    });
  }

  getEnvironmentSettings(): any {
    if (environmentSettingsConfig[this.stateService.getProfile()]) {
      return environmentSettingsConfig[this.stateService.getProfile()][
        this.stateService.getEnvironment().toUpperCase()
      ];
    }
    return environmentSettingsConfig[ProjectProfile.ACME][
      this.stateService.getEnvironment().toUpperCase()
    ];
  }

  getEnvironmentProjectInfo(): ProjectInfo {
    return this.stateService.getProjectInfo();
  }

  async decrypt(data: string): Promise<string> {
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
    const raw = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));

    const iv = raw.slice(0, 12);
    const ciphertext = raw.slice(12);

    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(environment.configSecretKey),
      'AES-GCM',
      false,
      ['decrypt'],
    );

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      ciphertext,
    );

    return new TextDecoder().decode(decrypted);
  }
}
